/**
 * User Service
 * 
 * This service handles all user-related API calls.
 * It uses the privateApiClient for authenticated requests.
 * For admin operations, the user must have superuser privileges.
 */

import { privateApiClient } from '../apiClient';
import { handleApiError } from '../apiClient';
import API from '../../config/api';

/**
 * Get all users (admin only)
 * @param {Object} params - Query parameters (skip, limit, etc.)
 * @returns {Promise<Array>} List of users
 */
export const getAllUsers = async (params = {}) => {
  try {
    const response = await privateApiClient.get(API.users.base, { params });
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch users');
  }
};

/**
 * Get user by ID
 * @param {string} id - User ID
 * @returns {Promise<Object>} User data
 */
export const getUserById = async (id) => {
  try {
    const response = await privateApiClient.get(API.users.getById(id));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch user');
  }
};

/**
 * Create new user (admin only)
 * @param {Object} userData - User data to create
 * @returns {Promise<Object>} Created user data
 */
export const createUser = async (userData) => {
  try {
    const response = await privateApiClient.post(API.users.base, userData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to create user');
  }
};

/**
 * Update user (admin only for other users, users can update their own data)
 * @param {string} id - User ID
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} Updated user data
 */
export const updateUser = async (id, userData) => {
  try {
    const response = await privateApiClient.put(API.users.update(id), userData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to update user');
  }
};

/**
 * Delete user (admin only)
 * @param {string} id - User ID
 * @returns {Promise<Object>} Response data
 */
export const deleteUser = async (id) => {
  try {
    const response = await privateApiClient.delete(API.users.delete(id));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to delete user');
  }
};

/**
 * Reset user password (admin only)
 * @param {string} id - User ID
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response data
 */
export const resetUserPassword = async (id, newPassword) => {
  try {
    const response = await privateApiClient.post(
      `${API.users.base}/${id}/reset-password`,
      { new_password: newPassword }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to reset user password');
  }
};

/**
 * Get current user profile
 * @returns {Promise<Object>} Current user data
 */
export const getCurrentUser = async () => {
  try {
    const response = await privateApiClient.get(`${API.users.base}/me`);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch current user');
  }
};

/**
 * Update current user profile
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} Updated user data
 */
export const updateCurrentUser = async (userData) => {
  try {
    const response = await privateApiClient.put(`${API.users.base}/me`, userData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to update profile');
  }
};

/**
 * Change user role
 * @param {string} id - User ID
 * @param {string} role - New role
 * @returns {Promise<Object>} Updated user data
 */
export const changeUserRole = async (id, role) => {
  try {
    const response = await privateApiClient.patch(API.users.changeRole(id), { role });
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to change user role');
  }
};

/**
 * Get all committee members
 * @returns {Promise<Array>} List of committee members
 */
export const getAllCommitteeMembers = async () => {
  try {
    const response = await privateApiClient.get(API.users.committee.base);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch committee members');
  }
};

/**
 * Get committee member by ID
 * @param {string} id - Committee member ID
 * @returns {Promise<Object>} Committee member data
 */
export const getCommitteeMemberById = async (id) => {
  try {
    const response = await privateApiClient.get(API.users.committee.getById(id));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch committee member');
  }
};

// Export all functions as a single object
const userService = {
  // Admin functions
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  
  // Regular user functions
  getUserById,
  getCurrentUser,
  updateCurrentUser,
  changeUserRole,
  getAllCommitteeMembers,
  getCommitteeMemberById
};

export default userService;
